import React, { FC, MouseEvent, useState } from 'react'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import MuiLink from '@mui/material/Link'
import { Link } from 'react-router-dom'
import SupervisedUserCircle from '@mui/icons-material/AccountCircle'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import LogOutIcon from '@mui/icons-material/LogoutOutlined'
import Typography from '@mui/material/Typography'
import MuiMenu from '@mui/material/Menu'
import { Box, Divider, useMediaQuery, useTheme } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import useUser from '@context/UserContext/useUser'
import { PATIENTS, PROVIDERS } from '../../route.const'
import ClinicianSignIn from './ClinicianSignIn'
import { styled } from '@mui/system'
import { KeyboardArrowUp } from '@mui/icons-material'
import { neutral } from '../../theme/base/theme'
import LogInMenuData from '../../config-data/home/menu-list.json'
import ProfileAvatar from './ProfileAvatar'
import { useHeaderAuth } from './useHeaderAuth'
import { SIGN_IN_MENU_OPTIONS } from './constants'
import useAdobeAnalytics, {
  CLINICIAN_EVENTS,
  SHARED_EVENTS,
} from '@analytics/adobe'
import { useFlags } from 'launchdarkly-react-client-sdk'

const MuiMenuStyled = styled(MuiMenu)({
  '& .MuiPaper-root': {
    marginLeft: '-20px',
  },
  marginTop: 3,
})

const UserMenu: FC<{
  username: string
  isLogin: boolean
  degrees: string
  avatar: string
}> = ({ username, isLogin, degrees, avatar }) => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null)
  const { breakpoints } = useTheme()
  const isMobile = useMediaQuery(breakpoints.down('md'))
  const { role } = useUser()
  const [open, setOpen] = useState(false)
  const [navigateURL, setNavigateURL] = useState('')
  const postLoginMenuItems = LogInMenuData.data

  const { handleLogout, handleLoginClick, trackLink, login } = useHeaderAuth()
  const { trackPage } = useAdobeAnalytics()
  const { enableHomeDesignV2: enableHomeDesignV2Flag } = useFlags() as {
    enableHomeDesignV2?: boolean
  }
  const enableHomeDesignV2: boolean = enableHomeDesignV2Flag ?? false

  const handleOpenNavMenu = (event: MouseEvent<HTMLElement>) => {
    // Always use om_link_click:sign_in_clinician for clinician sign-in, never just 'clinician'
    let tag = ''
    switch (username.trim().toLowerCase()) {
      case 'clinician':
        tag = SHARED_EVENTS.LINK_CLICK.SIGN_IN_CLINICIAN
        break
      case 'patient':
        tag = SHARED_EVENTS.LINK_CLICK.SIGN_IN_PATIENT
        break
      case 'sign in':
        tag = SHARED_EVENTS.LINK_CLICK.SIGN_IN
        break
      default:
        if (
          typeof window !== 'undefined' &&
          window.location.pathname === '/providers/login'
        ) {
          tag = SHARED_EVENTS.LINK_CLICK.SIGN_IN_CLINICIAN
        }
        break
    }
    if (tag) {
      trackLink(
        {
          name: tag,
          location: 'unauthenticated-headers',
          type: 'internal',
        },
        {
          loginStatus: login ? 'logged in' : 'not authorized',
          role: role,
        }
      )
    }
    // Do NOT send a second linktrack here to avoid overriding the sign-in click event
    setAnchorElNav(event.currentTarget)
  }

  const handleCloseNavMenu = () => {
    setAnchorElNav(null)
  }

  const handleClose = () => {
    setAnchorElNav(null)
  }

  const handleCloseAll = () => {
    trackLink(
      {
        name: 'Sign out',
        location: 'User menu',
        type: 'internal',
      },
      {
        loginStatus: login ? 'logged in' : 'logged out',
        userType: role,
      }
    )
    handleLogout(username)
    handleCloseNavMenu()
  }

  // Handle login click with dialog support - call original logic, then track
  const handleLoginWithDialog = (url: string, title: string) => {
    const onDialogOpen = () => {
      setOpen(true)
      handleCloseNavMenu()
      setNavigateURL(url)
    }

    // Call the original handleLoginClick which has all the core logic
    handleLoginClick(url, title, onDialogOpen)

    // Track AFTER the core logic has been initiated
    let tag = ''
    if (title && title.trim().toLowerCase() === 'clinician') {
      tag = SHARED_EVENTS.LINK_CLICK.SIGN_IN_CLINICIAN
    } else if (title && title.trim().toLowerCase() === 'patient') {
      tag = SHARED_EVENTS.LINK_CLICK.SIGN_IN_PATIENT
    }

    if (tag) {
      trackLink(
        {
          name: tag,
          location: 'unauthenticated-headers',
          type: 'url',
          destinationUrl: url,
        },
        {
          loginStatus: login ? 'logged in' : 'not authorized',
          role: role,
        }
      )
    }
  }

  const buildLoggedInMenuItems = () => {
    const items: React.ReactNode[] = []

    if (role === PROVIDERS) {
      items.push(
        <MenuItem key="provider-profile" style={{ whiteSpace: 'normal' }}>
          <Box display={'flex'} width={'250px'}>
            <ProfileAvatar avatar={avatar} username={username} size="medium" />
            <Box display={'flex'} gap={1} flexDirection={'column'} ml={2}>
              <Typography
                minWidth={'100px'}
                component="div"
                display="inline"
                sx={{ display: 'block', wordBreak: 'break-word' }}
              >
                <Box display="inline">
                  <Typography
                    display="inline"
                    variant="h6"
                    sx={{ marginBlockStart: 0, marginBlockEnd: 0 }}
                  >
                    {username}
                  </Typography>
                </Box>
                {degrees && (
                  <Typography display="inline">{`, ${degrees.replace(
                    /[ ,]/g,
                    ', '
                  )}`}</Typography>
                )}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
      )
      items.push(<Divider key="provider-profile-divider" component="li" />)
    }

    if (role === PATIENTS) {
      items.push(
        <MenuItem
          key="patient-profile"
          style={{ whiteSpace: 'normal', display: 'none' }}
        >
          <Box display={'flex'} width={'250px'}>
            <ProfileAvatar avatar={avatar} username={username} size="medium" />
            <Box
              display={'flex'}
              gap={1}
              flexDirection={'column'}
              ml={2}
              pt={1}
            >
              <Typography
                minWidth={'100px'}
                component="div"
                display="inline"
                sx={{ display: 'block', wordBreak: 'break-word' }}
              >
                <Box display="inline">
                  <Typography
                    display="inline"
                    variant="h6"
                    sx={{ marginBlockStart: 0, marginBlockEnd: 0 }}
                  >
                    {username}
                  </Typography>
                </Box>
                {degrees && (
                  <Typography display="inline">{`, ${degrees.replace(
                    /[ ,]/g,
                    ', '
                  )}`}</Typography>
                )}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
      )
    }

    postLoginMenuItems.forEach((row: any, index: number) => {
      const base = `${row.path || row.label || index}`
      if (row.showLine) {
        items.push(
          <Divider
            key={`divider-${base}-${index}`}
            component="li"
            sx={{ pb: 1 }}
          />
        )
      }
      if (!row.signOut) {
        items.push(
          <MenuItem
            key={`menu-${base}-${index}`}
            sx={({ ues }) => ({
              mt: { xs: 'auto', md: -1 },
              mb: -1,
              padding: 2,
              pl: 5,
              color: ues.neutral['80'],
            })}
          >
            <MuiLink
              component={Link}
              sx={({ ues }) => ({
                color: ues.neutral['80'],
                '&:visited': { color: neutral['80'] },
                textDecoration: 'none',
                outline: 'none',
                justifyContent: 'space-between',
                display: 'flex',
                gap: '4px',
                width: '100%',
              })}
              onClick={() => {
                // Check if clinician is logged in
                const isClinician = login && role === PROVIDERS

                // Map label to appropriate tags
                let pageTag = ''
                let linkTag = row.label
                const labelLower = row.label.trim().toLowerCase()

                if (isClinician) {
                  // Use clinician-specific tags
                  switch (labelLower) {
                    case 'settings':
                      linkTag = CLINICIAN_EVENTS.LINK_CLICK.MENU_SETTINGS
                      break
                    case 'about optum match':
                    case 'about us':
                      linkTag = CLINICIAN_EVENTS.LINK_CLICK.MENU_ABOUT_US
                      break
                    case 'privacy policy':
                      pageTag = CLINICIAN_EVENTS.PAGE.PRIVACY_POLICY
                      linkTag = CLINICIAN_EVENTS.LINK_CLICK.MENU_PRIVACY_POLICY
                      break
                    case 'terms of use':
                      pageTag = CLINICIAN_EVENTS.PAGE.TERMS_OF_USE
                      linkTag = CLINICIAN_EVENTS.LINK_CLICK.MENU_TERMS_OF_USE
                      break
                    case 'faq':
                      pageTag = CLINICIAN_EVENTS.PAGE.FAQ
                      linkTag = CLINICIAN_EVENTS.LINK_CLICK.MENU_FAQ
                      break
                    case 'contact us':
                      pageTag = CLINICIAN_EVENTS.PAGE.CLINICAL_ASSISTANCE
                      linkTag = CLINICIAN_EVENTS.LINK_CLICK.MENU_CONTACT_US
                      break
                    case 'clinical assistance':
                      pageTag = CLINICIAN_EVENTS.PAGE.CLINICAL_ASSISTANCE
                      linkTag =
                        CLINICIAN_EVENTS.LINK_CLICK.MENU_CLINICAL_ASSISTANCE
                      break
                  }
                }

                // Track link click event
                trackLink(
                  {
                    name: linkTag,
                    location: isClinician
                      ? 'authenticated-clinician-headers'
                      : 'User menu',
                    type: row.external ? 'external' : 'internal',
                    description: 'User can view the ' + row.label,
                  },
                  {
                    loginStatus: login ? 'logged in' : 'logged out',
                    userType: role,
                  }
                )

                // Track page view event
                if (pageTag) {
                  trackPage(
                    {
                      name: pageTag,
                      is404: false,
                    },
                    {
                      loginStatus: login ? 'logged in' : 'logged out',
                      userType: role,
                    }
                  )
                }

                setAnchorElNav(null)
              }}
              target={row.external ? '_blank' : '_self'}
              rel={row.external ? 'noopener noreferrer' : 'undefined'}
              to={
                row.external ? `${window.location.origin}${row.path}` : row.path
              }
            >
              {row.label}
            </MuiLink>
          </MenuItem>
        )
      } else {
        items.push(
          <React.Fragment key={`signout-${base}-${index}`}>
            <Divider component={'li'} sx={{ pb: 1 }} />
            <MenuItem
              key={`signout-item-${base}-${index}`}
              onClick={handleCloseAll}
              sx={{ mt: { xs: 'auto' }, mb: -1, padding: 2, pl: 5, gap: 0.5 }}
            >
              <LogOutIcon />
              Sign out
            </MenuItem>
          </React.Fragment>
        )
      }
    })

    return items
  }

  const buildLoggedOutMenuItems = () =>
    SIGN_IN_MENU_OPTIONS.map((row, index) => {
      return (
        <MenuItem
          key={`signin-${index}`}
          onClick={() => {
            // Single unified function - executes core logic first, then tracks
            handleLoginWithDialog(row.url, row.title)
          }}
          sx={({ ues }) => ({
            flexDirection: 'row',
            minWidth: '250px',
            fontSize: '16px',
            fontWeight: 400,
            color: ues.neutral['80'],
            pt: 1,
            pb: 1,
            '&:hover': {
              color: `${ues.primary[70]} !important`,
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          })}
        >
          {row.description}
        </MenuItem>
      )
    })

  return (
    <>
      {open && (
        <ClinicianSignIn open={open} setOpen={setOpen} url={navigateURL} />
      )}
      {isMobile && role !== PROVIDERS ? (
        <IconButton
          color={'primary'}
          onClick={handleOpenNavMenu}
          sx={{
            display: {
              xs: 'none',
              sm: 'none',
              md: 'flex',
              lg: 'flex',
              xl: 'flex',
            },
          }}
        >
          <SupervisedUserCircle />
          <KeyboardArrowDownIcon fontSize="small" />
        </IconButton>
      ) : (
        <Button
          variant={!isLogin && enableHomeDesignV2 ? 'contained' : undefined}
          onClick={handleOpenNavMenu}
          startIcon={
            isLogin && role !== PROVIDERS ? <SupervisedUserCircle /> : undefined
          }
          endIcon={
            Boolean(anchorElNav) ? (
              <KeyboardArrowUp />
            ) : (
              <KeyboardArrowDownIcon />
            )
          }
          sx={({ ues }) => ({
            display: {
              xs: 'none',
              sm: 'none',
              md: 'flex',
              lg: 'flex',
              xl: 'flex',
            },
            m: 0,
            mt: enableHomeDesignV2 ? 0 : -0.5,
            ...(enableHomeDesignV2
              ? {
                  alignSelf: 'center',
                  ...(!isLogin
                    ? {
                        borderRadius: '50px',
                        px: 2.5,
                        py: '6px',
                        fontSize: '14px',
                        fontWeight: 700,
                        lineHeight: '24px',
                        textTransform: 'none',
                        backgroundColor: 'transparent',
                        color: ues.neutral['80'],
                        border: `2px solid ${ues.neutral['80']}`,
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: ues.neutral['80'],
                          border: `2px solid ${ues.neutral['80']}`,
                          boxShadow: 'none',
                        },
                        '&:active': {
                          backgroundColor: 'transparent',
                          color: ues.neutral['80'],
                        },
                      }
                    : {
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: ues.primary['100'],
                        },
                      }),
                }
              : {
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: ues.primary['100'],
                  },
                }),
          })}
        >
          {role === PROVIDERS && isLogin ? (
            <ProfileAvatar avatar={avatar} username={username} size="medium" />
          ) : (
            <Typography
              sx={({ ues }) => ({
                display: { xs: 'none', sm: 'block' },
                maxWidth: 300,
                overflowX: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: undefined,
                overflow: 'hidden',
                fontSize: !isLogin ? '16px' : '18px',
                fontWeight: !isLogin ? '700' : 'inherit',
                textDecoration: 'none',
                textAlign: 'left',
                color: ues.neutral['80'],
                '&:hover': {
                  color:
                    !isLogin && enableHomeDesignV2
                      ? ues.neutral['80']
                      : ues.primary['100'],
                },
              })}
            >
              {username}
            </Typography>
          )}
        </Button>
      )}

      <MuiMenuStyled
        anchorEl={anchorElNav}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElNav)}
        onClose={handleCloseNavMenu}
        onClick={handleClose}
      >
        <MenuList
          aria-labelledby="composition-button"
          sx={{ p: 0, minWidth: '350px' }}
        >
          {isLogin ? buildLoggedInMenuItems() : buildLoggedOutMenuItems()}
        </MenuList>
      </MuiMenuStyled>
    </>
  )
}

export default UserMenu
